import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { classificationToken, nodeKindToken, type Classification, type NodeKind } from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Card.module.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Draws the 3px classification rail. Give it a kind or a classification. */
  kind?: NodeKind;
  classification?: Classification;
  /** `top` for a catalogue card, `left` for a list row. */
  railEdge?: 'top' | 'left' | 'none';
  /** Hover/active affordance. The caller supplies the real control inside. */
  interactive?: boolean;
  selected?: boolean;
  padding?: 'none' | 'compact' | 'default';
  /** Raised, for a floating palette item under drag. */
  elevated?: boolean;
  children: ReactNode;
}

/**
 * The container everything sits in: layer-02 on a 1px hairline, square corners,
 * no shadow. The classification rail is the card's only colour, and it always
 * accompanies a visible id or kind label somewhere inside.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    kind,
    classification,
    railEdge = kind === undefined && classification === undefined ? 'none' : 'top',
    interactive = false,
    selected = false,
    padding = 'default',
    elevated = false,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const rail =
    kind !== undefined
      ? nodeKindToken(kind)
      : classification !== undefined
        ? classificationToken(classification, 'base')
        : undefined;

  const vars: StyleWithVars = {};
  if (rail !== undefined) vars['--dpem-card-rail'] = rail;

  return (
    <div
      {...rest}
      ref={ref}
      data-kind={kind}
      data-selected={selected ? 'true' : undefined}
      className={clsx(
        styles.card,
        padding !== 'none' && (padding === 'compact' ? styles.compact : styles.padded),
        railEdge === 'top' && rail !== undefined && styles.railTop,
        railEdge === 'left' && rail !== undefined && styles.railLeft,
        interactive && styles.interactive,
        selected && styles.selected,
        elevated && styles.elevated,
        className,
      )}
      style={mergeStyle(vars, style)}
    >
      {children}
    </div>
  );
});

// `title` is a heading node here, not the DOM's tooltip string attribute.
export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Small uppercase kicker — the node kind or canonical id. */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Right-aligned slot: badges, an overflow menu. */
  actions?: ReactNode;
  children?: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { eyebrow, title, actions, className, children, ...rest },
  ref,
) {
  return (
    <div {...rest} ref={ref} className={clsx(styles.header, className)}>
      <div className={styles.headerText}>
        {eyebrow !== undefined && <p className={styles.eyebrow}>{eyebrow}</p>}
        <h3 className={styles.title}>{title}</h3>
        {children}
      </div>
      {actions !== undefined && <div className={styles.actions}>{actions}</div>}
    </div>
  );
});

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, children, ...rest }, ref) {
    return (
      <div {...rest} ref={ref} className={clsx(styles.body, className)}>
        {children}
      </div>
    );
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, children, ...rest }, ref) {
    return (
      <div {...rest} ref={ref} className={clsx(styles.footer, className)}>
        {children}
      </div>
    );
  },
);
